import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Tabs,
  Tab,
  Badge,
  Table,
  Modal,
  Form,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../Context/AuthContext";
import { fetchCustomerByCodeAndOrg } from "../Api/ViewCustomerMaster";
import {
  fetchCusVenCpByCusVenType,
  createCusVenCp,
} from "../Api/CusVenCpMasterApi";

const PURPLE = "#6f42c1";

/* ================= INFO TILE ================= */
const InfoItem = ({ label, value }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body>
      <small className="text-muted fw-semibold">{label}</small>
      <div className="fw-semibold mt-1">{value || "-"}</div>
    </Card.Body>
  </Card>
);

const ViewCustomerDetails = () => {
  const { customerCode } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  /* ===== MEMBER LIST ===== */
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [cpForm, setCpForm] = useState({
    contactPerName: "",
    contactPerNumber: "",
    contactPerEmail: "",
  });

  /* ================= LOAD CUSTOMER ================= */
  useEffect(() => {
    const loadCustomer = async () => {
      try {
        const data = await fetchCustomerByCodeAndOrg(
          customerCode,
          user.organisationId
        );
        setCustomer(data);
      } catch {
        toast.error("Failed to load customer details");
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [customerCode, user]);

  /* ================= LOAD MEMBERS ================= */
  const loadMembers = async () => {
    try {
      setLoadingMembers(true);
      const data = await fetchCusVenCpByCusVenType(customerCode);
      setMembers(data);
    } catch (error) {
      toast.error("Failed to load contact persons");
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (activeTab === "members") loadMembers();
  }, [activeTab]);

  /* ================= ADD MEMBER ================= */
  const handleSaveMember = async () => {
    try {
      await createCusVenCp(
        {
          cusVenTypeId: customerCode,
          contactPerName: cpForm.contactPerName,
          contactPerNumber: cpForm.contactPerNumber,
          contactPerEmail: cpForm.contactPerEmail,
          orgId: String(user.organisationId),
        },
        user.userId
      );

      toast.success("Contact person added successfully");
      setShowModal(false);
      setCpForm({
        contactPerName: "",
        contactPerNumber: "",
        contactPerEmail: "",
      });
      loadMembers();
    } catch {
      toast.error("Failed to add contact person");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" style={{ color: PURPLE }} />
      </div>
    );
  }

  if (!customer) {
    return <p className="text-center mt-4">Customer not found</p>;
  }

  return (
    <div>
      {/* ================= HEADER ================= */}
      <div
        style={{
          background: "linear-gradient(135deg, #6f7ae0, #7b5fc4)",
          color: "#fff",
          padding: "28px 32px",
        }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h3 className="fw-bold mb-1">{customer.name}</h3>
            <div className="opacity-75">
              Customer Code: {customer.customerCode}
            </div>

            <div className="mt-3 d-flex gap-2 flex-wrap">
              <Badge bg="light" text="dark">
                {customer.customerType}
              </Badge>
              <Badge bg="success">{customer.active}</Badge>
              <Badge bg="secondary">{customer.currencyCode}</Badge>
            </div>
          </div>

          <Button variant="light" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        </div>
      </div>

      {/* ================= TABS BAR ================= */}
      <div style={{ background: "#fff", borderBottom: "1px solid #eee" }}>
        <Container fluid>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="pt-2"
            variant="underline"
          >
            <Tab
              eventKey="overview"
              title="Overview"
              //   style={{ padding: "10px" }}
            />
            <Tab
              eventKey="members"
              title="Member List"
              //   style={{ padding: "10px" }}
            />
          </Tabs>
        </Container>
      </div>

      {/* ================= CONTENT ================= */}
      <Container fluid className="mt-4">
        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === "overview" && (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <InfoItem label="Short Name" value={customer.shortNames} />
                </Col>
                <Col md={4}>
                  <InfoItem label="City" value={customer.cityName} />
                </Col>
                <Col md={4}>
                  <InfoItem label="Region" value={customer.regionName} />
                </Col>

                <Col md={4}>
                  <InfoItem label="GST Number" value={customer.gstno} />
                </Col>
                <Col md={4}>
                  <InfoItem label="PAN" value={customer.pan} />
                </Col>
                <Col md={4}>
                  <InfoItem
                    label="Payment Mode"
                    value={customer.paymentTransaction}
                  />
                </Col>

                <Col md={4}>
                  <InfoItem label="Entered By" value={customer.enteredBy} />
                </Col>
                <Col md={4}>
                  <InfoItem
                    label="Entered Date"
                    value={moment(customer.enteredDateTime).format("LL")}
                  />
                </Col>
                <Col md={4}>
                  <InfoItem label="Country" value={customer.countryCode} />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* ===== MEMBER LIST TAB ===== */}
        {activeTab === "members" && (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between mb-3">
                <h6 className="fw-semibold">Contact Persons</h6>
                <Button
                  style={{ backgroundColor: PURPLE, border: "none" }}
                  onClick={() => setShowModal(true)}
                >
                  + Add Member
                </Button>
              </div>

              {loadingMembers ? (
                <Spinner animation="border" />
              ) : members.length > 0 ? (
                <Table hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Number</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, i) => (
                      <tr key={m.cusVenId}>
                        <td>{i + 1}</td>
                        <td>{m.contactPerName}</td>
                        <td>{m.contactPerNumber}</td>
                        <td>{m.contactPerEmail}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No contact persons found.</p>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* ================= ADD MEMBER MODAL ================= */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Contact Person</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={cpForm.contactPerName}
                onChange={(e) =>
                  setCpForm({ ...cpForm, contactPerName: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Number</Form.Label>
              <Form.Control
                value={cpForm.contactPerNumber}
                onChange={(e) =>
                  setCpForm({ ...cpForm, contactPerNumber: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={cpForm.contactPerEmail}
                onChange={(e) =>
                  setCpForm({ ...cpForm, contactPerEmail: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: PURPLE, border: "none" }}
            onClick={handleSaveMember}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewCustomerDetails;

// import { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import {
//   Container,
//   Card,
//   Row,
//   Col,
//   Button,
//   Spinner,
//   Tabs,
//   Tab,
//   Badge,
//   Table,
//   Modal,
//   Form,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";
// import { useAuth } from "../Context/AuthContext";
// import { fetchCustomerByCodeAndOrg } from "../Api/ViewCustomerMaster";
// import {
//   fetchCusVenCpByCusVenType,
//   createCusVenCp,
// } from "../Api/CusVenCpMasterApi";

// const PURPLE = "#6f42c1";

// /* ===== REUSABLE INFO CARD ===== */
// const InfoItem = ({ label, value }) => (
//   <Card className="border-0 shadow-sm mb-3 h-100">
//     <Card.Body>
//       <small className="text-muted fw-semibold">{label}</small>
//       <div className="fw-semibold mt-1">{value || "-"}</div>
//     </Card.Body>
//   </Card>
// );

// const ViewCustomerDetails = () => {
//   const { customerCode } = useParams();
//   const { user } = useAuth();
//   const navigate = useNavigate();

//   const [customer, setCustomer] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [activeTab, setActiveTab] = useState("basic");

//   /* ===== MEMBER LIST ===== */
//   const [members, setMembers] = useState([]);
//   const [loadingMembers, setLoadingMembers] = useState(false);
//   const [showModal, setShowModal] = useState(false);

//   const [cpForm, setCpForm] = useState({
//     contactPerName: "",
//     contactPerNumber: "",
//     contactPerEmail: "",
//   });

//   /* ===== LOAD CUSTOMER ===== */
//   useEffect(() => {
//     const loadCustomer = async () => {
//       try {
//         const data = await fetchCustomerByCodeAndOrg(
//           customerCode,
//           user.organisationId
//         );
//         setCustomer(data);
//       } catch (error) {
//         toast.error("Failed to load customer details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadCustomer();
//   }, [customerCode, user]);

//   /* ===== LOAD MEMBERS ===== */
//   const loadMembers = async () => {
//     try {
//       setLoadingMembers(true);

//       const data = await fetchCusVenCpByCusVenType(customerCode);

//       // ✅ If API returns empty or null → valid case
//       if (!data || data.length === 0) {
//         setMembers([]);
//         return;
//       }

//       setMembers(data);
//     } catch (error) {
//       // ✅ Show error ONLY if it's not "no data"
//       if (error?.status !== 404) {
//         toast.error("Failed to load contact persons");
//       }
//       setMembers([]);
//     } finally {
//       setLoadingMembers(false);
//     }
//   };

//   useEffect(() => {
//     if (activeTab === "members") loadMembers();
//   }, [activeTab]);

//   /* ===== ADD MEMBER ===== */
//   const handleSaveMember = async () => {
//     try {
//       const payload = {
//         cusVenTypeId: customerCode,
//         contactPerName: cpForm.contactPerName,
//         contactPerNumber: cpForm.contactPerNumber,
//         contactPerEmail: cpForm.contactPerEmail,
//         orgId: String(user.organisationId),
//       };

//       await createCusVenCp(payload, user.userId);

//       toast.success("Contact person added successfully");
//       setShowModal(false);
//       setCpForm({
//         contactPerName: "",
//         contactPerNumber: "",
//         contactPerEmail: "",
//       });
//       loadMembers();
//     } catch {
//       toast.error("Failed to add contact person");
//     }
//   };

//   if (loading) {
//     return (
//       <div className="text-center mt-5">
//         <Spinner animation="border" style={{ color: PURPLE }} />
//       </div>
//     );
//   }

//   if (!customer) {
//     return <p className="text-center mt-4">Customer not found</p>;
//   }

//   return (
//     <Container fluid className="mt-3">
//       {/* ===== BACK ===== */}
//       <Button variant="light" className="mb-2" onClick={() => navigate(-1)}>
//         ← Back
//       </Button>

//       {/* ===== HEADER ===== */}
//       <Card
//         className="border-0 shadow-sm mb-3"
//         style={{
//           background: `linear-gradient(135deg, ${PURPLE}, #8a63d2)`,
//           color: "#fff",
//         }}
//       >
//         <Card.Body>
//           <Row className="align-items-center">
//             <Col md={6}>
//               <h4 className="mb-1">{customer.name}</h4>
//               <small className="opacity-75">
//                 Customer Code: {customer.customerCode}
//               </small>
//             </Col>

//             <Col md={3}>
//               <strong>📞 Mobile</strong>
//               <div>{customer.mobileNumber || "-"}</div>
//             </Col>

//             <Col md={3}>
//               <strong>Status</strong>
//               <div>
//                 <Badge bg={customer.active === "Yes" ? "success" : "secondary"}>
//                   {customer.active}
//                 </Badge>
//               </div>
//             </Col>
//           </Row>
//         </Card.Body>
//       </Card>

//       {/* ===== TABS ===== */}
//       <Card className="border-0 shadow-sm">
//         <Card.Body>
//           <Tabs
//             activeKey={activeTab}
//             onSelect={(k) => setActiveTab(k)}
//             variant="pills"
//             className="mb-3"
//           >
//             {/* ================= BASIC INFO ================= */}
//             <Tab eventKey="basic" title="📄 Basic Information">
//               <Row>
//                 <Col md={4}>
//                   <InfoItem label="Short Name" value={customer.shortNames} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem
//                     label="Customer Type"
//                     value={customer.customerType}
//                   />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Currency" value={customer.currencyCode} />
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={4}>
//                   <InfoItem
//                     label="GST Registered"
//                     value={customer.registeredInGst}
//                   />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="GST Number" value={customer.gstno} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="PAN" value={customer.pan} />
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={4}>
//                   <InfoItem label="City" value={customer.cityName} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Region" value={customer.regionName} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Pincode" value={customer.pincode} />
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={4}>
//                   <InfoItem label="Country" value={customer.countryCode} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Lead Type" value={customer.leadType} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem
//                     label="Lead Reference"
//                     value={customer.leadReference}
//                   />
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={4}>
//                   <InfoItem
//                     label="Payment Mode"
//                     value={customer.paymentTransaction}
//                   />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Entered By" value={customer.enteredBy} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem
//                     label="Entered Date"
//                     value={moment(customer.enteredDateTime).format("L")}
//                   />
//                 </Col>
//               </Row>

//               <Row>
//                 <Col md={4}>
//                   <InfoItem label="Address Line 1" value={customer.address1} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Address Line 2" value={customer.address2} />
//                 </Col>
//                 <Col md={4}>
//                   <InfoItem label="Address Line 3" value={customer.address3} />
//                 </Col>
//               </Row>
//             </Tab>

//             {/* ================= MEMBER LIST ================= */}
//             <Tab eventKey="members" title="👥 Member List">
//               <div className="d-flex justify-content-between mb-3">
//                 <h6 className="fw-semibold">Contact Persons</h6>
//                 <Button
//                   style={{ backgroundColor: PURPLE, border: "none" }}
//                   onClick={() => setShowModal(true)}
//                 >
//                   + Add Member
//                 </Button>
//               </div>

//               {loadingMembers ? (
//                 <Spinner animation="border" />
//               ) : members.length > 0 ? (
//                 <Table bordered hover>
//                   <thead className="table-light">
//                     <tr>
//                       <th>#</th>
//                       <th>Name</th>
//                       <th>Number</th>
//                       <th>Email</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {members.map((m, i) => (
//                       <tr key={m.cusVenId}>
//                         <td>{i + 1}</td>
//                         <td>{m.contactPerName}</td>
//                         <td>{m.contactPerNumber}</td>
//                         <td>{m.contactPerEmail}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </Table>
//               ) : (
//                 <p className="text-muted">No contact persons found.</p>
//               )}
//             </Tab>
//           </Tabs>
//         </Card.Body>
//       </Card>

//       {/* ===== ADD MEMBER MODAL ===== */}
//       <Modal show={showModal} onHide={() => setShowModal(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Add Contact Person</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Name</Form.Label>
//               <Form.Control
//                 value={cpForm.contactPerName}
//                 onChange={(e) =>
//                   setCpForm({ ...cpForm, contactPerName: e.target.value })
//                 }
//               />
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Label>Number</Form.Label>
//               <Form.Control
//                 value={cpForm.contactPerNumber}
//                 onChange={(e) =>
//                   setCpForm({ ...cpForm, contactPerNumber: e.target.value })
//                 }
//               />
//             </Form.Group>

//             <Form.Group>
//               <Form.Label>Email</Form.Label>
//               <Form.Control
//                 value={cpForm.contactPerEmail}
//                 onChange={(e) =>
//                   setCpForm({ ...cpForm, contactPerEmail: e.target.value })
//                 }
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowModal(false)}>
//             Cancel
//           </Button>
//           <Button
//             style={{ backgroundColor: PURPLE, border: "none" }}
//             onClick={handleSaveMember}
//           >
//             Save
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </Container>
//   );
// };

// export default ViewCustomerDetails;
